import classnames from 'classnames';
import { ChangeEvent, useEffect, useState } from 'react';

import styles from './Checkbox.module.scss';

export type ConditionalProps =
  | {
      /**
       * If true, the onChange call back will receive a
       * boolean instead of a ChangeEvent
       */
      provideValueOnChange: true;
      onChange?: (value: boolean) => void;
    }
  | {
      provideValueOnChange?: false;
      onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    };

export type CheckboxProps = {
  /**
   * Will display a label if provided
   */
  label?: string;
  /**
   * This set's the default value for the checkbox
   */
  defaultChecked?: boolean;
  /**
   * Use this if managing the checked state externally
   * for example as a react-hook-forms controller
   */
  checked?: boolean;
  /**
   * Provide a name if you're using the checkbox inside a form
   */
  name?: string;
  /**
   * Custom style class to extend the checkbox
   */
  className?: string;
  /**
   * Custom style class to extend the checkbox label text
   */
  labelTextClassName?: string;
  /**
   * Flag to disable the checkbox
   */
  disabled?: boolean;
  /**
   * Test id for Cypress and other test suites, maps to `data-testid`
   */
  testId?: string;
  appearance?: 'small' | 'medium' | 'big';
} & ConditionalProps;

const Checkbox = ({
  label,
  defaultChecked,
  checked,
  className,
  labelTextClassName,
  name,
  disabled,
  onChange,
  provideValueOnChange,
  testId,
  appearance = 'small',
}: CheckboxProps) => {
  const [isChecked, setIsChecked] = useState<boolean>(defaultChecked || false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    const checked = event?.target?.checked;
    setIsChecked(checked);

    if (provideValueOnChange) onChange?.(checked);
    else {
      onChange?.(event);
    }
  };

  useEffect(() => {
    if (typeof checked === 'boolean') {
      setIsChecked(checked);
    }
  }, [checked]);

  const containerStyles = classnames(
    styles.container,
    className,
    { [styles.disabled]: disabled },
    { [styles.medium]: appearance === 'medium' },
    { [styles.big]: appearance === 'big' },
  );

  return (
    <label
      className={containerStyles}
      role="checkbox"
      data-testid={testId}
      aria-disabled={disabled}
      aria-checked={isChecked}
      aria-label={name}>
      <input
        type="checkbox"
        className={styles.input}
        onChange={handleChange}
        checked={isChecked}
        disabled={disabled}
        name={name}
        role="none"
      />
      <span className={styles.checkmark}></span>
      {label && <span className={classnames(styles.label, labelTextClassName)}>{label}</span>}
    </label>
  );
};

export { Checkbox };
